import { GlassPanel } from '../ui/GlassPanel';

// ── Param definitions ────────────────────────────────────────────────────────

const WIND_PARAMS = [
  { key: 'windSpeed',     label: 'Wind',       min: 20,  max: 400,  step: 10,   unit: '' },
  { key: 'turbulence',    label: 'Turbulence', min: 0,   max: 200,  step: 5,    unit: '' },
  { key: 'gustCycle',     label: 'Gust cycle', min: 0.5, max: 20,   step: 0.5,  unit: 's' },
  { key: 'gustAmplitude', label: 'Gust amp',   min: 0,   max: 1,    step: 0.05, unit: '×' },
  { key: 'verticalDrift', label: 'V. drift',   min: 0,   max: 120,  step: 5,    unit: '' },
];

const PARTICLE_PARAMS = [
  { key: 'sizeMin',    label: 'Size min', min: 0.5, max: 5,  step: 0.5,  unit: 'px' },
  { key: 'sizeMax',    label: 'Size max', min: 1,   max: 12, step: 0.5,  unit: 'px' },
  { key: 'opacityMin', label: 'α min',   min: 0,   max: 1,  step: 0.05, unit: '' },
  { key: 'opacityMax', label: 'α max',   min: 0,   max: 1,  step: 0.05, unit: '' },
];

const FLOW_PARAMS = [
  { key: 'flowStrength',   label: 'Thermal', min: 20,  max: 500, step: 10, unit: '' },
  { key: 'vortexStrength', label: 'Vortex',  min: 0,   max: 400, step: 10, unit: '' },
  { key: 'flowRadius',     label: 'Radius',  min: 40,  max: 400, step: 10, unit: 'px' },
  { key: 'flowDrift',      label: 'Drift',   min: 0,   max: 60,  step: 2,  unit: '' },
];

const MOUSE_PARAMS = [
  { key: 'mouseRadius',     label: 'Radius', min: 10, max: 100, step: 5,    unit: 'px' },
  { key: 'mouseStrength',   label: 'Force',  min: 50, max: 800, step: 10,   unit: '' },
  { key: 'mouseSwirlRatio', label: 'Swirl',  min: 0,  max: 1,   step: 0.05, unit: '' },
  { key: 'mouseBounce',     label: 'Bounce', min: 0,  max: 1,   step: 0.05, unit: '' },
];

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <span className="text-[8px] font-semibold tracking-[0.2em] uppercase text-[var(--color-muted)]/40">
      {children}
    </span>
  );
}

function Divider() {
  return <div className="border-t border-white/[0.05]" />;
}

function ParamRow({ label, value, min, max, step, unit, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-16 shrink-0 text-[9px] tracking-wide uppercase text-[var(--color-muted)]/55 truncate">
        {label}
      </span>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="flex-1 h-[3px] cursor-pointer accent-[var(--color-accent)]"
      />
      <span className="w-12 text-right text-[10px] text-[var(--color-text)] tabular-nums shrink-0">
        {value}{unit}
      </span>
    </div>
  );
}

function StatBox({ label, value, warn }) {
  return (
    <div className={[
      'flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl border',
      warn
        ? 'border-orange-400/35 bg-orange-400/[0.07]'
        : 'border-white/[0.06] bg-white/[0.03]',
    ].join(' ')}>
      <span className="text-[8px] uppercase tracking-widest text-[var(--color-muted)]/45">
        {label}
      </span>
      <span className={[
        'text-base font-mono font-bold leading-none',
        warn ? 'text-orange-400' : 'text-[var(--color-accent)]',
      ].join(' ')}>
        {value}
      </span>
    </div>
  );
}

function Toggle({ label, checked, onChange, active }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox" checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="accent-[var(--color-accent)] w-3 h-3 shrink-0"
      />
      <span className="text-[10px] text-[var(--color-muted)]">{label}</span>
      {active && (
        <span className="ml-auto text-[9px] text-[var(--color-accent)]/70 animate-pulse">
          active
        </span>
      )}
    </label>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SandstormDebugPanel({
  config, onUpdate,
  mouseEnabled, onToggleMouse,
  flowsEnabled, onToggleFlows,
  stats, onReset,
}) {
  const fpWarn = stats.fps > 0 && stats.fps < 50;
  const msWarn = parseFloat(stats.frameMs) > 5;

  return (
    <GlassPanel className="flex-1 flex flex-col overflow-hidden">

      {/* ── Pinned header ── */}
      <div className="flex-none flex flex-col gap-2 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[9px] font-semibold tracking-[0.18em] uppercase text-[var(--color-muted)]/70">
            Sandstorm Debug
          </h2>
          <button
            onClick={onReset}
            className="
              text-[9px] px-2 py-0.5 rounded-md
              border border-white/[0.08] bg-white/[0.04]
              text-[var(--color-muted)] hover:text-[var(--color-text)]
              hover:border-[var(--color-accent)]/30
              transition-colors cursor-pointer
            "
          >
            ↺ Reset
          </button>
        </div>
        <Divider />
      </div>

      {/* ── Scrollable middle ── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col gap-2.5 py-1">

        <SectionLabel>Wind</SectionLabel>
        <div className="flex flex-col gap-1.5">
          {WIND_PARAMS.map(p => (
            <ParamRow key={p.key} {...p} value={config[p.key]} onChange={v => onUpdate(p.key, v)} />
          ))}
        </div>

        <Divider />

        <div className="flex items-center justify-between">
          <SectionLabel>Particle</SectionLabel>
          {stats.aliveCount != null && stats.aliveCount < stats.particleCount && (
            <span className="text-[9px] font-mono tabular-nums text-[var(--color-accent)]/70">
              {stats.aliveCount}/{stats.particleCount}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {/* Count — applies on Reset (triggers component remount) */}
          <div className="flex items-center gap-1.5">
            <span className="w-16 shrink-0 text-[9px] tracking-wide uppercase text-[var(--color-muted)]/55 truncate">
              Count
            </span>
            <input
              type="range" min={256} max={2048} step={256}
              value={config.count}
              onChange={e => onUpdate('count', Number(e.target.value))}
              className="flex-1 h-[3px] cursor-pointer accent-[var(--color-accent)]"
            />
            <span className="w-12 text-right text-[10px] text-[var(--color-text)] tabular-nums shrink-0">
              {config.count}
            </span>
          </div>
          {PARTICLE_PARAMS.map(p => (
            <ParamRow key={p.key} {...p} value={config[p.key]} onChange={v => onUpdate(p.key, v)} />
          ))}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="w-16 shrink-0 text-[9px] tracking-wide uppercase text-[var(--color-muted)]/55">
              Color
            </span>
            <input
              type="color" value={config.color}
              onChange={e => onUpdate('color', e.target.value)}
              className="w-8 h-4 cursor-pointer rounded border-0 bg-transparent p-0"
              style={{ WebkitAppearance: 'none' }}
            />
            <span className="text-[9px] text-[var(--color-muted)]/45 font-mono">{config.color}</span>
          </div>
        </div>

        <Divider />

        <Toggle
          label="Fluid dynamics (thermals + vortices)"
          checked={flowsEnabled}
          onChange={onToggleFlows}
          active={flowsEnabled}
        />
        {flowsEnabled && (
          <div className="flex flex-col gap-1.5 pl-2 border-l-2 border-[var(--color-accent)]/20">
            <div className="flex items-center gap-1.5">
              <span className="w-16 shrink-0 text-[9px] tracking-wide uppercase text-[var(--color-muted)]/55">
                Thermals
              </span>
              <input
                type="range" min={1} max={4} step={1}
                value={config.numFlows}
                onChange={e => onUpdate('numFlows', Number(e.target.value))}
                className="flex-1 h-[3px] cursor-pointer accent-[var(--color-accent)]"
              />
              <span className="w-12 text-right text-[10px] text-[var(--color-text)] tabular-nums shrink-0">
                {config.numFlows}
              </span>
            </div>
            {FLOW_PARAMS.map(p => (
              <ParamRow key={p.key} {...p} value={config[p.key]} onChange={v => onUpdate(p.key, v)} />
            ))}
            <p className="text-[8px] text-[var(--color-muted)]/30 leading-tight mt-0.5">
              Hot=↑ converge · Cold=↓ diverge · Rankine vortex ⊥ radius
            </p>
          </div>
        )}

        <Divider />

        <Toggle
          label="Mouse disturbance"
          checked={mouseEnabled}
          onChange={onToggleMouse}
          active={mouseEnabled}
        />
        {mouseEnabled && (
          <div className="flex flex-col gap-1.5 pl-2 border-l-2 border-[var(--color-accent)]/20">
            {MOUSE_PARAMS.map(p => (
              <ParamRow key={p.key} {...p} value={config[p.key]} onChange={v => onUpdate(p.key, v)} />
            ))}
          </div>
        )}

      </div>

      {/* ── Pinned footer ── */}
      <div className="flex-none flex flex-col gap-2 px-4 pt-2 pb-4">
        <Divider />
        <div className="flex gap-2">
          <StatBox label="FPS"    value={stats.fps || '—'}                 warn={fpWarn} />
          <StatBox label="CPU ms" value={stats.fps ? stats.frameMs : '—'} warn={msWarn} />
        </div>
        <p className="text-[8px] text-[var(--color-muted)]/25 leading-tight">
          size/α apply on wrap · count: {config.count}
        </p>
      </div>

    </GlassPanel>
  );
}
